import camelcaseKeys from 'camelcase-keys'
import throttle from 'lodash.throttle'
import { create } from 'zustand'

import { MILITAO_URL } from '@/config'
import {
  executeGet,
  executePatch,
  executePost,
  executePut,
} from '@/services/lukita'
import { createWithMutating } from '@/stores'
import { getProjectPath, getUrl } from '@/utils/routing'
import { getCreateAccountUrl, getWelcomeUrl } from '../auth/utils/onboarding'
import { MissingAccountError } from '../errors'
import type {
  Account,
  AccountDTO,
  AccountSummary,
  AccountSummaryDTO,
} from './types'

const THROTTLE_MS = 500

export interface AccountStoreActions {
  reset: () => void
  startLoading: () => void
  stopLoadingAccount: () => void
  stopLoadingSummary: () => void
  setAccount: (account: Account | AccountDTO) => void
  setSummary: (summary: AccountSummary | AccountSummaryDTO) => void
}

export interface CreateAccountParams {
  priceId: string
  name: string
  searchParams: URLSearchParams
}

interface RenewProjectParams {
  slug: string
}

interface AccountStoreMutations {
  createAccount: (params: CreateAccountParams) => Promise<Account>
  selectPlan: (priceId: string) => Promise<string>
  purchaseProject: (project?: RenewProjectParams) => Promise<string>
  updateAccount: (body: Pick<AccountDTO, 'name'>) => Promise<void>
}

interface AccountStoreLoaders {
  fetchAccount: () => Promise<void>
  fetchAccountSummary: () => Promise<void>
  fetchCustomerPortalUrl: () => Promise<Account['customerPortalUrl']>
}

interface ChatUsage {
  counter: string
  progress: number
  minProgress: 2
  hasCap: boolean
  exceeded: boolean
  walletBalanceCents: number
  walletExpiresAt: Date | string | null
}

interface AccountStore {
  loadingAccount: boolean
  loadingSummary: boolean
  account: Account | null
  summary: AccountSummary | null
  mutating: boolean
  createdAt: string
  usage: {
    minProgress: 2
    hasLimitedPages?: boolean
    progress?: number
    counter?: string
  }
  chatUsage: ChatUsage
  actions: AccountStoreActions
  mutations: AccountStoreMutations
  loaders: AccountStoreLoaders
}

const CHAT_MIN_PROGRESS = 2 as const

const EMPTY_CHAT_USAGE: ChatUsage = {
  counter: '',
  progress: 0,
  minProgress: CHAT_MIN_PROGRESS,
  hasCap: false,
  exceeded: false,
  walletBalanceCents: 0,
  walletExpiresAt: null,
}

const formatDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`

// Client mirror of lukita's Billing::ChatCreditEnforcer::ENFORCEMENT_ENABLED.
// Chat is unlimited for now — we shipped unlimited chat instead of finishing
// the Stripe chat-credit integration, and may revisit it. `computeChatUsage`
// below still computes the real predicate (kept intact + tested); this flag
// is applied once, where the store builds `chatUsage`, so every consumer
// (composer gate AND the billing-usage meter) sees the disabled state from a
// single source. The server's gate is authoritative and disabled too, so the
// client side is just the proactive UI. Flip both flags to re-enable.
export const CHAT_ENFORCEMENT_ENABLED = false

// Mirrors the pages-usage convention: `progress` is the REMAINING
// portion (bar fills toward empty as the user consumes budget),
// floored at `minProgress` so the bar never disappears entirely.
// `theme === 'error'` triggers when `progress === minProgress` —
// same display rule as pages.
//
// Capped mode: drain bar against the entitlement cap, with counter
// and "Resets in …" below.
// Wallet-only mode: show the same bar in its emptied (red) state so
// the visual stays consistent — the entitlement is gone but credits
// keep the chat alive. No counter, no cycle line; the credits line
// is the primary info.
//
// `exceeded` mirrors the lukita enforcer's gate: `used >= cap + wallet`,
// guarded by an unenforced early-return for accounts with no cap AND
// no wallet. The server also locks accounts whose wallet has drained
// (post-monetization lock); the SPA can't see "ever purchased" from
// the summary alone, so this is a UI hint and the server's 402 is
// authoritative — the composer's existing 402 handler picks up the
// drained-wallet case for free.
export const computeChatUsage = (summary: AccountSummary): ChatUsage => {
  const limit = summary.chatCostCentsPerMonthLimit
  const used = summary.monthlyChatCostCents
  const walletBalanceCents = summary.chatCreditsBalanceCents ?? 0
  const walletExpiresAt = summary.chatCreditsNextExpiry ?? null
  const hasCap = limit > 0

  if (!hasCap && walletBalanceCents === 0) return EMPTY_CHAT_USAGE

  const cap = hasCap ? limit : 0
  const exceeded = used >= cap + walletBalanceCents

  if (!hasCap) {
    return {
      counter: '',
      progress: CHAT_MIN_PROGRESS,
      minProgress: CHAT_MIN_PROGRESS,
      hasCap: false,
      exceeded,
      walletBalanceCents,
      walletExpiresAt,
    }
  }

  const counter = `${formatDollars(used)} / ${formatDollars(limit)}`
  const ratio = used / cap
  const progress = Math.max(CHAT_MIN_PROGRESS, (1 - ratio) * 100)
  return {
    counter,
    progress,
    minProgress: CHAT_MIN_PROGRESS,
    hasCap,
    exceeded,
    walletBalanceCents,
    walletExpiresAt,
  }
}

export const fetchAccount = async ({ withCustomerPortal = false } = {}) => {
  const response = await executeGet(`/accounts`, {
    queryParams: withCustomerPortal ? { with_customer_portal: true } : {},
  })
  const accounts = await response.json()
  if (accounts.length === 0) throw new MissingAccountError()
  return camelcaseKeys(accounts[0])
}

export const useAccountStore = create<AccountStore>((set, get, store) => {
  const withMutating = createWithMutating(set)

  return {
    loadingAccount: true,
    loadingSummary: true,
    account: null,
    summary: null,
    mutating: false,
    createdAt: '',
    usage: { minProgress: 2 },
    chatUsage: EMPTY_CHAT_USAGE,

    actions: {
      reset: () => set(store.getInitialState()),
      startLoading: () => set({ loadingAccount: true, loadingSummary: true }),
      stopLoadingAccount: () => set({ loadingAccount: false }),
      stopLoadingSummary: () => set({ loadingSummary: false }),
      setAccount: (account) =>
        set({ account: camelcaseKeys(account) as Account }),
      setSummary: (data) => {
        const summary = camelcaseKeys(data)
        const minProgress = get().usage.minProgress
        const hasLimitedPages = summary.pagesPerMonthLimit !== -1
        const counter = hasLimitedPages
          ? `${summary.monthlyPagesProcessed} / ${summary.pagesPerMonthLimit}`
          : 'Unlimited'
        const ratio = summary.monthlyPagesProcessed / summary.pagesPerMonthLimit
        const progress = Math.max(minProgress, (1 - ratio) * 100)
        set({
          summary,
          usage: { hasLimitedPages, progress, counter, minProgress },
          // Single enforcement choke point: when chat is unlimited, every
          // consumer reads the empty usage so neither the composer gate nor
          // the billing meter acts on a cap that no longer applies.
          chatUsage: CHAT_ENFORCEMENT_ENABLED
            ? computeChatUsage(summary)
            : EMPTY_CHAT_USAGE,
        })
      },
    },

    mutations: {
      createAccount: ({ priceId, name, searchParams }) =>
        withMutating(async () => {
          const response = await executePost('/accounts', {
            account: {
              name,
              price_id: priceId,
              success_url: getWelcomeUrl(),
              cancel_url: getCreateAccountUrl(searchParams),
            },
          })
          const account = await response.json()
          return camelcaseKeys(account) as Account
        }),

      selectPlan: (priceId) =>
        withMutating(async () => {
          const response = await executePut('/accounts/select_plan', {
            account: {
              price_id: priceId,
              success_url: getWelcomeUrl(),
              cancel_url: MILITAO_URL,
            },
          })
          const account = await response.json()
          return account.checkout_url
        }),

      purchaseProject: (project) =>
        withMutating(async () => {
          const returnUrl = project
            ? getUrl(getProjectPath({ slug: project.slug }))
            : getUrl('projects')
          const response = await executePost('/accounts/purchase_project', {
            success_url: returnUrl,
            cancel_url: returnUrl,
            ...(project && { project_slug: project.slug }),
          })
          const account = await response.json()
          return account.checkout_url
        }),

      updateAccount: (attrs) =>
        withMutating(async () => {
          const response = await executePatch(`/accounts/`, attrs)
          const account = await response.json()
          get().actions.setAccount(account)
        }),
    },

    loaders: {
      fetchAccount: throttle(async () => {
        const { account, actions } = get()
        if (!account) actions.startLoading()
        const updatedAccount = await fetchAccount()
        actions.setAccount(updatedAccount)
        actions.stopLoadingAccount()
      }, THROTTLE_MS),

      fetchAccountSummary: throttle(async () => {
        const { account, actions } = get()
        if (!account) {
          actions.stopLoadingSummary()
          return
        }
        const response = await executeGet(`/accounts/${account.id}/summary`)
        const summary = await response.json()
        actions.setSummary(summary)
        actions.stopLoadingSummary()
      }, THROTTLE_MS),

      fetchCustomerPortalUrl: async () => {
        const account = await fetchAccount({ withCustomerPortal: true })
        return account.customerPortalUrl
      },
    },
  }
})

export const useLoadingAccount = () =>
  useAccountStore((state) => state.loadingAccount)

export const useAccount = () => useAccountStore((state) => state.account)

export const getAccount = () => useAccountStore.getState().account

export const useAccountMutating = () =>
  useAccountStore((state) => state.mutating)

export const useLoadingSummary = () =>
  useAccountStore((state) => state.loadingSummary)

export const useSummary = () => useAccountStore((state) => state.summary)

export const useUsage = () => useAccountStore((state) => state.usage)

export const useChatUsage = () => useAccountStore((state) => state.chatUsage)

export const useAccountActions = () => useAccountStore((state) => state.actions)
export const useAccountMutations = () =>
  useAccountStore((state) => state.mutations)
export const useAccountLoaders = () => useAccountStore((state) => state.loaders)
